import { _isNotEmpty } from '@/littledash';
import type { Finding, SendValidationResult } from '@/model/Agent.model';
import { useMemo, useState, type FC } from 'react';
import SendDisclosureList from './SendDisclosureList';
import SendFilterChips from './SendFilterChips';
import { toggled } from '@/components/Agents/SendValidator/utils/SendFilterChips';
import SendFindingsTable from './SendFindingsTable';
import SendHeadlineBanner from './SendHeadlineBanner';
import SendOmissionNotice from './SendOmissionNotice';
import SendSectionHeader from './SendSectionHeader';
import { disclosuresOf, partitionFindings } from '@/components/Agents/SendValidator/utils/SendFindingPartition';
import {
  bySeverityLevel,
  severityChipsOf,
  severityCountsOf,
  severityLevelOf,
} from '@/components/Agents/SendValidator/utils/SendSeverity';
import { aiNotesCount } from '@/components/Agents/SendValidator/utils/SendAiUse';
import { fileHeadlineOf } from '@/components/Agents/SendValidator/utils/SendHeadline';
import { omissionOf } from '@/components/Agents/SendValidator/utils/SendOmission';
import { countOf, findingLocationOf } from '@/components/Agents/SendValidator/utils/SendText';
import { sendDatasetLabel, sendDatasetOf } from '@/components/Agents/SendValidator/utils/SendDomains';
import type { SendFileRuleCheck } from '@/components/Agents/SendValidator/utils/SendRuleOutcomes';
import styles from './SendResults.module.scss';

interface SendResultsGridProps {
  result: SendValidationResult;
  /** What the rules made of this file, from the server's tally; null on results stored before it. */
  ruleCheck?: SendFileRuleCheck | null;
  /** Whether Benchling In Vivo AI took part in the run, so a suggestion may be its own. */
  aiUsed?: boolean;
  /**
   * How many uploaded files were confirmed to this file's dataset. The rule tally is the dataset's, so
   * when it is more than one, a rule that failed may have its findings in another of those files.
   */
  datasetFiles?: number;
  /** The section's position in the report, shown before its title. */
  sectionNumber?: number;
  testId?: string;
}

const bySeverityThenRow = (a: Finding, b: Finding): number => {
  const severityDelta = bySeverityLevel(a, b);
  if (severityDelta !== 0) {
    return severityDelta;
  }
  return (a.row_number ?? 0) - (b.row_number ?? 0);
};

/**
 * One file's section of a SEND report: its dataset and rows, the server's own numbers for it, a
 * headline keyed on the submission-blocking count, and the checks' findings in a table filtered by
 * severity. A check's notice that it could not decide something is listed apart, drawn neutrally and
 * never filtered or counted as a finding. Benchling In Vivo AI notes are not shown here: they have
 * their own section, and this one only says how many there are.
 *
 * Every number reads the server's exact counts, never the findings the result carries, and the
 * omission notice announces whatever a limit left out.
 */
const SendResultsGrid: FC<SendResultsGridProps> = ({
  result,
  ruleCheck,
  aiUsed = false,
  datasetFiles = 1,
  sectionNumber,
  testId = 'invivo-send-validator__results',
}) => {
  const { findings, counts, advisory, row_count: rowCount } = result;
  const datasetName = sendDatasetOf(result);
  const severityCounts = useMemo(() => severityCountsOf(result), [result]);
  // Only a published grading's rejections block, and none exists for SEND in M1.
  const blocking = severityCounts?.reject ?? 0;
  // What the checks found, not what the result carries: a capped file must not understate itself here.
  const foundCount = counts.finding_groups;
  const undecidedChecks = counts.undecided_checks;
  // Every AI note, shown or not. They are advisory, so they never turn the file into a failure, but a
  // success headline must still say they are there to read.
  const aiNotes = advisory.observations;
  const headline = fileHeadlineOf({
    datasetName,
    blocking,
    found: foundCount,
    aiNotes,
    datasetFiles,
    ruleCheck: ruleCheck ?? null,
  });

  const parts = useMemo(() => partitionFindings(findings), [findings]);
  const sortedChecks = useMemo(() => [...parts.checks].sort(bySeverityThenRow), [parts.checks]);
  const disclosures = useMemo(() => disclosuresOf(parts.disclosures, '', findingLocationOf), [parts.disclosures]);

  const [severities, setSeverities] = useState<ReadonlySet<string>>(() => new Set());
  const shownChecks = useMemo(
    () =>
      severities.size === 0 ? sortedChecks : sortedChecks.filter((finding) => severities.has(severityLevelOf(finding))),
    [sortedChecks, severities]
  );
  const severityChips = useMemo(() => severityChipsOf(severityCounts, sortedChecks), [severityCounts, sortedChecks]);
  const hasChecks = _isNotEmpty(sortedChecks);

  return (
    <div
      className="ui-card pa4"
      data-test-component="SendResultsGrid"
      data-test-element="container"
      data-testid={testId}
    >
      <SendSectionHeader
        as="h3"
        number={sectionNumber}
        title={
          <>
            {sendDatasetLabel(datasetName)}{' '}
            <span className={styles.sectionMeta}>· {countOf(rowCount, 'row', 'rows')}</span>
          </>
        }
        severityCounts={severityCounts}
        exactPass={headline.tone === 'success'}
        titleTestId="invivo-send-validator__results-meta"
      />

      <p className={styles.sectionCounts} data-test-element="server-counts">
        <span data-test-element="count-findings">{countOf(foundCount, 'finding', 'findings')}</span>
        {undecidedChecks > 0 && (
          <span data-test-element="count-undecided">
            {' · '}
            {countOf(undecidedChecks, "check couldn't decide", "checks couldn't decide")}
          </span>
        )}
        {aiNotes > 0 && (
          <span data-test-element="count-ai-notes">
            {' · '}
            {aiNotesCount(aiNotes)}, shown in their own section
          </span>
        )}
      </p>

      <SendHeadlineBanner
        headline={headline}
        testId="invivo-send-validator__results-banner"
        headlineTestId="invivo-send-validator__results-headline"
      />

      {/* AI notes are counted in their own section; this one announces only the checks' own omissions. */}
      <SendOmissionNotice findings={omissionOf(counts, advisory)} testId="invivo-send-validator__omission" />

      {hasChecks && (
        <div className="mt3">
          <SendFilterChips
            label={`Filter ${sendDatasetLabel(datasetName)} findings by severity`}
            options={severityChips}
            selected={severities}
            onToggle={(key) => setSeverities((current) => toggled(current, key))}
            testId="invivo-send-validator__severity-filter"
          />
          <div className="mt2">
            {_isNotEmpty(shownChecks) ? (
              <SendFindingsTable
                // A new filter starts at the first page.
                key={[...severities].sort().join(',')}
                findings={shownChecks}
                aiUsed={aiUsed}
                testId="invivo-send-validator__grid"
              />
            ) : (
              <p className="f6 dark-gray ma0" data-test-element="filter-empty">
                None of the findings shown here has the selected severity.
              </p>
            )}
          </div>
        </div>
      )}

      <SendDisclosureList
        disclosures={disclosures}
        undecidedChecks={undecidedChecks}
        testId="invivo-send-validator__disclosures"
      />
    </div>
  );
};

export default SendResultsGrid;
